/**
 * URL-backed query state + data loading for the Team Admin user list.
 *
 * The whole query (search, persona filter, sort, page, page size, "show
 * removed") lives in the address bar, so a browser refresh, a bookmark and a
 * shared link all restore the same view — and the browser back button steps
 * through filter changes the way people expect.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useApiClient } from '../../app/providers';
import { ApiError } from '../../lib/api-error';
import type { SeatUsage, TeamUser, TeamUserPage } from '../../lib/api-types';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../../lib/constants';
import { PERSONAS, type Persona } from '../../lib/personas';
import {
  boolParam,
  enumParam,
  intChoiceParam,
  intParam,
  readUrlState,
  textParam,
  writeUrlState,
  URL_PARAM_KEYS,
} from '../../lib/url-state';

export const USER_SORT_FIELDS = ['email', 'persona', 'display_name', 'created_at'] as const;
export type UserSortField = (typeof USER_SORT_FIELDS)[number];

export interface TeamUsersQueryState {
  search: string;
  persona: Persona | 'all';
  sortBy: UserSortField;
  sortOrder: 'asc' | 'desc';
  page: number;
  pageSize: number;
  isShowingRemoved: boolean;
}

/** Declarative screen state (shared url-state kit): parse is total, defaults stay off the URL. */
const TEAM_USERS_URL_STATE = {
  search: textParam(URL_PARAM_KEYS.search),
  persona: enumParam('persona', [...PERSONAS, 'all'] as const, 'all'),
  sortBy: enumParam(URL_PARAM_KEYS.sortBy, USER_SORT_FIELDS, 'email'),
  sortOrder: enumParam(URL_PARAM_KEYS.sortOrder, ['asc', 'desc'] as const, 'asc'),
  page: intParam(URL_PARAM_KEYS.page, { fallback: 1, min: 1 }),
  pageSize: intChoiceParam(URL_PARAM_KEYS.pageSize, PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE),
  isShowingRemoved: boolParam('removed'),
};

function readQuery(params: URLSearchParams): TeamUsersQueryState {
  return readUrlState(TEAM_USERS_URL_STATE, params);
}

export interface TeamUsersController {
  query: TeamUsersQueryState;
  /** Merge a partial query. Anything but an explicit page change resets to page 1. */
  setQuery: (patch: Partial<TeamUsersQueryState>) => void;
  users: readonly TeamUser[];
  total: number;
  totalPages: number;
  seats: SeatUsage | null;
  isLoading: boolean;
  error: string | null;
  reload: () => void;
}

export function useTeamUsers(): TeamUsersController {
  const apiClient = useApiClient();
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState<TeamUserPage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const query = useMemo(() => readQuery(params), [params]);

  const setQuery = useCallback(
    (patch: Partial<TeamUsersQueryState>) => {
      const next = { ...readQuery(params), ...patch };
      if (patch.page === undefined) next.page = 1;
      setParams(writeUrlState(TEAM_USERS_URL_STATE, next, params), { replace: true });
    },
    [params, setParams]
  );

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);

    apiClient
      .listTeamUsers(
        {
          q: query.search === '' ? undefined : query.search,
          persona: query.persona === 'all' ? undefined : query.persona,
          sort_by: query.sortBy,
          sort_order: query.sortOrder,
          page: query.page,
          limit: query.pageSize,
          include_inactive: query.isShowingRemoved ? true : undefined,
        },
        controller.signal
      )
      .then((result) => {
        if (controller.signal.aborted) return;
        setPage(result);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(
          ApiError.isApiError(caught)
            ? caught.message
            : 'The user list could not be loaded. Please try again.'
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [apiClient, query, reloadToken]);

  return {
    query,
    setQuery,
    users: page?.items ?? [],
    total: page?.total ?? 0,
    totalPages: page?.pagination.total_pages ?? 0,
    seats: page?.seats ?? null,
    isLoading,
    error,
    reload,
  };
}
